import db from '../db/index';
import moment from 'moment';
import { calculateMean, calculateSD } from '../helpers/calculateDistribution';
import { query, where, collection, doc, getDocs, getDoc } from 'firebase/firestore';

const getMonthString = (dateString) => {
    // dateString is in format 'YYYY-MM'
    const date = moment(dateString, 'YYYY-MM');

    // output of 'Jan', 'Feb', etc.
    return date.format('MMM');
};

const getMonthLabel = (dateString) => {
    // dateString is in format 'YYYY-MM-DD'
    const date = moment(dateString, 'YYYY-MM-DD');

    // output of "Jan 1", "Jan 2", etc.
    return date.format('MMM D');
};

const getCheckInPeriods = async uid => {
    const checkInsSnapshot = await getDocs(collection(db, 'profiles', uid, 'checkIns'));

    // "YYYY" => ["MMM", "MMM", ...]
    let periods = {};

    checkInsSnapshot.docs.forEach(doc => {
        const year = doc.id.split('-')[0];
        const month = getMonthString(doc.id);

        if (!periods[year]) {
            periods = {
                ...periods,
                [year]: [month],
            };
        } else {
            periods = {
                ...periods,
                [year]: [
                    ...periods[year],
                    month,
                ],
            };
        }

        return periods;
    });

    return periods;
};

const getCheckInDataByYearAndMonth = async(uid, year, month) => {
    const mood = {};
    const stress = {};
    const satisfaction = {};
    const tiredness = {};

    const monthString = moment(month, 'MMM').format('MM');
    const checkInsSnapshot = await getDoc(doc(db, 'profiles', uid, 'checkIns', `${year}-${monthString}`));
    const checkInData = checkInsSnapshot.data();

    if (checkInData) {
        const dates = Object.keys(checkInData);
        // sort dates in ascending order
        dates.sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);

            return dateA - dateB;
        });

        dates.forEach(date => {
            mood[getMonthLabel(date)] = checkInData[date].mood;
            stress[getMonthLabel(date)] = checkInData[date].stress;
            satisfaction[getMonthLabel(date)] = checkInData[date].satisfaction;
            tiredness[getMonthLabel(date)] = checkInData[date].tiredness;
        });
    }

    return {
        mood,
        stress,
        satisfaction,
        tiredness,
    };
};

const getCheckInDataForPastSevenDays = async(uid) => {
    const mood = {};
    const stress = {};
    const satisfaction = {};
    const tiredness = {};

    const today = moment();
    const year = today.format('YYYY');
    const month = today.format('MM');

    const checkInsSnapshot = await getDoc(doc(db, 'profiles', uid, 'checkIns', `${year}-${month}`));
    const checkInData = checkInsSnapshot.data();

    const dates = Object.keys(checkInData);

    dates.sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);

        return dateA - dateB;
    });

    const pastSevenDays = dates.filter(date => {
        const dateMoment = moment(date, 'YYYY-MM-DD');

        const diff = today.diff(dateMoment, 'days');
        return diff <= 7;
    });

    pastSevenDays.forEach(date => {
        mood[getMonthLabel(date)] = checkInData[date].mood;
        stress[getMonthLabel(date)] = checkInData[date].stress;
        satisfaction[getMonthLabel(date)] = checkInData[date].satisfaction;
        tiredness[getMonthLabel(date)] = checkInData[date].tiredness;
    });

    return {
        mood,
        stress,
        satisfaction,
        tiredness,
    };
};

const getCheckInDataForToday = async uid => {
    const scores = {};

    const today = moment();
    const monthYear = today.format('YYYY-MM');
    const monthYearDay = today.format('YYYY-MM-DD');

    const checkInSnapshot = await getDoc(doc(db, 'profiles', uid, 'checkIns', monthYear));
    const checkInData = checkInSnapshot.data();

    scores.mood = checkInData[monthYearDay].mood;
    scores.stress = checkInData[monthYearDay].stress;
    scores.satisfaction = checkInData[monthYearDay].satisfaction;
    scores.tiredness = checkInData[monthYearDay].tiredness;

    return scores;
};

const getZScoresByYearAndMonth = async(uid, year, month) => {
    const monthString = moment(month, 'MMM').format('MM');

    const scoresSnapshot = await getDoc(doc(db, 'scores', `${year}-${monthString}`));
    const scores = scoresSnapshot.data();

    const userCheckInSnapshot = await getDoc(doc(db, 'profiles', uid, 'scores', `${year}-${monthString}`));
    const userScores = userCheckInSnapshot.data();

    if (!scores || !userScores) {
        return null;
    }

    const zScores = {};
    zScores.mood = (userScores.moodMean - scores.moodMean) / scores.moodSD;
    zScores.stress = (userScores.stressMean - scores.stressMean) / scores.stressSD;
    zScores.satisfaction = (userScores.satisfactionMean - scores.satisfactionMean) / scores.satisfactionSD;
    zScores.tiredness = (userScores.tirednessMean - scores.tirednessMean) / scores.tirednessSD;

    return zScores;
};

const getZScoresForPastSevenDays = async uid => {
    const today = moment();
    const year = today.format('YYYY');
    const month = today.format('MM');
    const currentYearMonth = `${year}-${month}`;

    const scoresSnapshot = await getDoc(doc(db, 'weeklyScores', `${currentYearMonth}`));
    const scores = scoresSnapshot.data();

    const userCheckInSnapshot = await getDoc(doc(db, 'profiles', uid, 'weeklyScores', `${currentYearMonth}`));
    const userScores = userCheckInSnapshot.data();

    if (!scores || !userScores) {
        return null;
    }

    const zScores = {};
    zScores.mood = (userScores.moodMean - scores.moodMean) / scores.moodSD;
    zScores.stress = (userScores.stressMean - scores.stressMean) / scores.stressSD;
    zScores.satisfaction = (userScores.satisfactionMean - scores.satisfactionMean) / scores.satisfactionSD;
    zScores.tiredness = (userScores.tirednessMean - scores.tirednessMean) / scores.tirednessSD;

    return zScores;
};

const getZScoresForToday = async uid => {
    const moods = [];
    const stress = [];
    const satisfaction = [];
    const tiredness = [];

    const q = query(collection(db, 'profiles'), where('checkin', '==', true));
    const profileSnapshot = await getDocs(q);

    const today = moment();
    const monthYear = today.format('YYYY-MM');
    const monthYearDay = today.format('YYYY-MM-DD');

    for (const profile of profileSnapshot.docs) {
        const checkInSnapshot = await getDoc(doc(db, 'profiles', profile.id, 'checkIns', monthYear));
        const checkInData = checkInSnapshot.data();

        if (!checkInData) {
            continue;
        }

        moods.push(checkInData[monthYearDay].mood);
        stress.push(checkInData[monthYearDay].stress);
        satisfaction.push(checkInData[monthYearDay].satisfaction);
        tiredness.push(checkInData[monthYearDay].tiredness);
    }

    const moodMean = calculateMean(moods);
    const moodSD = calculateSD(moods, moodMean);

    const stressMean = calculateMean(stress);
    const stressSD = calculateSD(stress, stressMean);

    const satisfactionMean = calculateMean(satisfaction);
    const satisfactionSD = calculateSD(satisfaction, satisfactionMean);

    const tirednessMean = calculateMean(tiredness);
    const tirednessSD = calculateSD(tiredness, tirednessMean);

    const zScores = {};
    const userCheckInSnapshot = await getDoc(doc(db, 'profiles', uid, 'checkIns', monthYear));

    if (!userCheckInSnapshot.exists()) {
        return null;
    }

    const userCheckInData = userCheckInSnapshot.data();

    zScores.mood = (userCheckInData[monthYearDay].mood - moodMean) / moodSD;
    zScores.stress = (userCheckInData[monthYearDay].stress - stressMean) / stressSD;
    zScores.satisfaction = (userCheckInData[monthYearDay].satisfaction - satisfactionMean) / satisfactionSD;
    zScores.tiredness = (userCheckInData[monthYearDay].tiredness - tirednessMean) / tirednessSD;

    return zScores;
};

export {
    getCheckInPeriods,
    getCheckInDataByYearAndMonth,
    getCheckInDataForPastSevenDays,
    getCheckInDataForToday,
    getZScoresByYearAndMonth,
    getZScoresForPastSevenDays,
    getZScoresForToday,
};
